import api from '../../api/apiClient';
import { createResourceClient } from '../../api/resource';
import type { QuotationPdfFile } from '../admin/admin.api';

/**
 * Payroll & daily attendance (V28). Every rule lives in the database and every figure comes
 * from its stored procedures; these calls only fetch, edit inputs, and trigger a calculation.
 *
 *   /api/my/**          the signed-in employee: punch in/out, own attendance, own paid salary
 *   /api/attendance/**  HR: the month register, corrections, biometric import
 *   /api/payroll/**     setup (rules, components, PT, weekly offs, holidays, pay profiles) and runs
 */

// ── Shapes ─────────────────────────────────────────────────────────────────

export type RuleType = 'NUMBER' | 'TEXT' | 'TIME' | 'CHOICE';
export interface Rule {
  code: string;
  label: string;
  groupName: string;
  valueNum: number | null;
  valueText: string | null;
  valueType: RuleType;
  choices: string | null;
  description: string | null;
}

export type ComponentKind = 'EARNING' | 'DEDUCTION' | 'EMPLOYER';
export type ComponentMethod =
  | 'PCT_OF_GROSS'
  | 'PCT_OF_COMPONENT'
  | 'BALANCE'
  | 'FIXED'
  | 'PCT_OF_EARNINGS'
  | 'PT_SLAB'
  | 'EMPLOYEE_TDS'
  | 'ADVANCE';
export type Rounding = 'NONE' | 'NEAREST' | 'UP';
export type AppliesWhen = 'ALWAYS' | 'PF' | 'ESI' | 'PT';

export interface SalaryComponent {
  id: number;
  code: string;
  name: string;
  kind: ComponentKind;
  method: ComponentMethod;
  percent: number | null;
  baseCode: string | null;
  capAmount: number | null;
  thresholdAmount: number | null;
  fixedAmount: number | null;
  prorate: boolean;
  rounding: Rounding;
  appliesWhen: AppliesWhen;
  sortOrder: number;
  active: boolean;
  showOnPayslip: boolean;
}

export interface PtSlab {
  id: number;
  state: string;
  gender: 'ANY' | 'Male' | 'Female';
  minGross: number;
  maxGross: number | null;
  monthlyAmount: number;
  februaryAmount: number;
}

export interface WeeklyOff {
  id: number;
  dayOfWeek: number;
  weeksOfMonth: string;
  active: boolean;
}

export type HolidayKind = 'NATIONAL' | 'FESTIVAL' | 'COMPANY' | 'OPTIONAL';
export interface Holiday {
  id: number;
  holidayDate: string;
  name: string;
  kind: HolidayKind;
  isPaid: boolean;
}

export type PaymentMode = 'BANK' | 'CASH' | 'CHEQUE';
export interface Profile {
  pfApplicable: boolean;
  esiApplicable: boolean;
  ptApplicable: boolean;
  tdsMonthly: number | null;
  uanNo: string | null;
  esicNo: string | null;
  paymentMode: PaymentMode;
  bankAccountName: string | null;
  bankName: string | null;
  bankBranch: string | null;
  bankAccountNo: string | null;
  bankIfsc: string | null;
}

export interface EmployeePay {
  employeeId: number;
  code: string | null;
  name: string | null;
  department: string | null;
  designation: string | null;
  gender: string | null;
  joiningDate: string | null;
  leavingDate: string | null;
  employmentStatus: string | null;
  monthlyGross: number | null;
  grossEffectiveFrom: string | null;
  profile: Profile;
}

export interface Revision {
  id: number;
  effectiveFrom: string;
  monthlyGross: number;
  note: string | null;
  createdAt: string | null;
}

export type DayKind = 'WORKING' | 'WEEKLY_OFF' | 'HOLIDAY';
export type DayStatus =
  | 'PRESENT'
  | 'HALF_DAY'
  | 'ABSENT'
  | 'PAID_LEAVE'
  | 'UNPAID_LEAVE'
  | 'HALF_LEAVE'
  | 'WEEKLY_OFF'
  | 'HOLIDAY'
  | 'NOT_EMPLOYED'
  | 'UPCOMING'
  | 'IN_PROGRESS';
export type StatusOverride = 'PRESENT' | 'HALF_DAY' | 'ABSENT';

export interface AttendanceDay {
  date: string;
  weekday: string;
  dayKind: DayKind;
  holidayName: string | null;
  status: DayStatus;
  punchIn: string | null;
  punchOut: string | null;
  workedMinutes: number | null;
  isLate: boolean;
  leaveType: string | null;
  source: 'PUNCH' | 'BIOMETRIC' | 'HR' | null;
  override: string | null;
  remarks: string | null;
}

export interface AttendanceSummary {
  workingDays: number;
  weeklyOffs: number;
  holidays: number;
  present: number;
  halfDays: number;
  paidLeave: number;
  unpaidLeave: number;
  absent: number;
  lateMarks: number;
  lateLopDays: number;
  lopDays: number;
}

export interface AttendanceReport {
  employeeId: number;
  code: string | null;
  name: string | null;
  year: number;
  month: number;
  days: AttendanceDay[];
  summary: AttendanceSummary;
}

export interface Today {
  date: string;
  punchIn: string | null;
  punchOut: string | null;
  workedMinutes: number | null;
  status: DayStatus | string | null;
  canPunchIn: boolean;
  canPunchOut: boolean;
  officeStart: string | null;
  officeEnd: string | null;
}

export interface RegisterDayHead {
  date: string;
  weekday: string;
  dayKind: DayKind;
  holidayName: string | null;
}

export interface RegisterCell {
  date: string;
  status: DayStatus;
  punchIn: string | null;
  punchOut: string | null;
  workedMinutes: number | null;
  isLate: boolean;
  override: string | null;
  remarks: string | null;
}

export interface RegisterEmployee {
  employeeId: number;
  code: string | null;
  name: string | null;
  department: string | null;
  days: RegisterCell[];
  summary: AttendanceSummary;
}

export interface AttendanceRegister {
  year: number;
  month: number;
  days: RegisterDayHead[];
  employees: RegisterEmployee[];
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export type RunStatus = 'CALCULATED' | 'PAID';
export interface RunSummary {
  year: number;
  month: number;
  status: RunStatus;
  stale: boolean;
  employees: number;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
  totalEmployer: number;
  calculatedAt: string | null;
  calculatedByName: string | null;
  paidAt: string | null;
  paidByName: string | null;
  paymentDate: string | null;
  paymentReference: string | null;
}

export interface PayrollLine {
  id: number;
  employeeId: number;
  code: string | null;
  name: string | null;
  department: string | null;
  designation: string | null;
  monthlyGross: number;
  payDays: number;
  payableDays: number;
  lopDays: number;
  presentDays: number;
  paidLeaveDays: number;
  unpaidLeaveDays: number;
  absentDays: number;
  lateMarks: number;
  earnedGross: number;
  totalDeductions: number;
  netPay: number;
  employerContribution: number;
  paymentMode: PaymentMode | string | null;
  bankAccountNo: string | null;
  bankIfsc: string | null;
  remarks: string | null;
}

export interface RunDetail extends RunSummary {
  lines: PayrollLine[];
}

export interface LineItem {
  code: string;
  name: string;
  kind: ComponentKind;
  fullAmount: number;
  amount: number;
}

export interface PayrollLineDetail extends PayrollLine {
  year: number;
  month: number;
  daysInMonth: number;
  workingDays: number;
  weeklyOffs: number;
  holidays: number;
  halfDays: number;
  lateLopDays: number;
  notEmployedDays: number;
  pan: string | null;
  uanNo: string | null;
  esicNo: string | null;
  bankName: string | null;
  items: LineItem[];
  runStatus: RunStatus;
}

export interface MySalaryMonth {
  year: number;
  month: number;
  netPay: number;
  earnedGross: number;
  totalDeductions: number;
  paymentDate: string | null;
}

// ── Calls ──────────────────────────────────────────────────────────────────

async function data<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  return (await p).data.data;
}

const runPath = (year: number, month: number) => `/api/payroll/runs/${year}/${month}`;

export const myApi = {
  today: () => data<Today>(api.get('/api/my/attendance/today')),
  punchIn: () => data<Today>(api.post('/api/my/attendance/punch-in')),
  punchOut: () => data<Today>(api.post('/api/my/attendance/punch-out')),
  attendance: (year: number, month: number) =>
    data<AttendanceReport>(api.get('/api/my/attendance', { params: { year, month } })),
  salaries: () => data<MySalaryMonth[]>(api.get('/api/my/salary')),
  salary: (year: number, month: number) => data<PayrollLineDetail>(api.get(`/api/my/salary/${year}/${month}`)),
  holidays: (year: number) => data<Holiday[]>(api.get('/api/holidays', { params: { year } })),
};

export interface DayCorrection {
  employeeId: number;
  date: string;
  punchIn?: string | null;
  punchOut?: string | null;
  statusOverride?: StatusOverride | null;
  remarks?: string | null;
}

export const attendanceApi = {
  register: (year: number, month: number) =>
    data<AttendanceRegister>(api.get('/api/attendance/register', { params: { year, month } })),
  employee: (employeeId: number, year: number, month: number) =>
    data<AttendanceReport>(api.get(`/api/attendance/employee/${employeeId}`, { params: { year, month } })),
  saveDay: (body: DayCorrection) => data<AttendanceDay>(api.put('/api/attendance/day', body)),
  importCsv: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return data<ImportResult>(api.post('/api/attendance/import', form, { headers: { 'Content-Type': 'multipart/form-data' } }));
  },
};

export const setupApi = {
  rules: () => data<Rule[]>(api.get('/api/payroll/rules')),
  saveRules: (rules: Pick<Rule, 'code' | 'valueNum' | 'valueText'>[]) => data<Rule[]>(api.put('/api/payroll/rules', rules)),
  employees: () => data<EmployeePay[]>(api.get('/api/payroll/employees')),
  saveProfile: (employeeId: number, profile: Profile) =>
    data<Profile>(api.put(`/api/payroll/employees/${employeeId}/profile`, profile)),
  revisions: (employeeId: number) => data<Revision[]>(api.get(`/api/payroll/employees/${employeeId}/revisions`)),
  addRevision: (employeeId: number, body: { effectiveFrom: string; monthlyGross: number; note?: string | null }) =>
    data<Revision>(api.post(`/api/payroll/employees/${employeeId}/revisions`, body)),
  deleteRevision: (employeeId: number, id: number) =>
    data<null>(api.delete(`/api/payroll/employees/${employeeId}/revisions/${id}`)),
};

export const componentsApi = createResourceClient<SalaryComponent>('/api/payroll/components');
export const ptSlabsApi = createResourceClient<PtSlab>('/api/payroll/pt-slabs');
export const weeklyOffsApi = createResourceClient<WeeklyOff>('/api/payroll/weekly-offs');
export const holidaysApi = createResourceClient<Holiday>('/api/payroll/holidays');

export const runsApi = {
  list: (year: number) => data<RunSummary[]>(api.get('/api/payroll/runs', { params: { year } })),
  get: (year: number, month: number) => data<RunDetail>(api.get(runPath(year, month))),
  calculate: (year: number, month: number) => data<RunDetail>(api.post(`${runPath(year, month)}/calculate`)),
  pay: (year: number, month: number, body: { paymentDate: string; reference?: string | null }) =>
    data<RunDetail>(api.post(`${runPath(year, month)}/pay`, body)),
  reopen: (year: number, month: number) => data<RunDetail>(api.post(`${runPath(year, month)}/reopen`)),
  line: (year: number, month: number, lineId: number) =>
    data<PayrollLineDetail>(api.get(`${runPath(year, month)}/lines/${lineId}`)),
};

/** GETs a file as a Blob, turning a JSON error body (which also arrives as a Blob) into the toast message. */
async function fetchBlob(url: string, params?: Record<string, unknown>): Promise<Blob> {
  const res = await api.get<Blob>(url, { params, responseType: 'blob' }).catch(async (error: any) => {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        error.userMessage = parsed?.message || parsed?.error || error.userMessage;
      } catch {
        // not JSON — keep the interceptor's message
      }
    }
    throw error;
  });
  return res.data;
}

const monthTag = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`;

/** Payslips for the month (everyone, or one employee) as a PDF for the in-page viewer. */
export async function fetchPayslipsPdf(year: number, month: number, employeeId?: number): Promise<QuotationPdfFile> {
  const blob = await fetchBlob(`${runPath(year, month)}/payslips.pdf`, employeeId ? { employeeId } : undefined);
  return {
    url: URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })),
    filename: `Payslips-${monthTag(year, month)}${employeeId ? `-${employeeId}` : ''}.pdf`,
  };
}

export async function fetchBankAdvicePdf(year: number, month: number): Promise<QuotationPdfFile> {
  const blob = await fetchBlob(`${runPath(year, month)}/bank-advice.pdf`);
  return {
    url: URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })),
    filename: `Salary-Bank-Advice-${monthTag(year, month)}.pdf`,
  };
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadBankStatement(year: number, month: number) {
  saveBlob(await fetchBlob(`${runPath(year, month)}/bank-statement.xlsx`), `Salary-Bank-Statement-${monthTag(year, month)}.xlsx`);
}

export async function downloadSalaryRegister(year: number, month: number) {
  saveBlob(await fetchBlob(`${runPath(year, month)}/register.xlsx`), `Salary-Register-${monthTag(year, month)}.xlsx`);
}

// ── Formatting & labels ────────────────────────────────────────────────────

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthLabel = (year: number, month: number) => `${MONTHS[month - 1]} ${year}`;

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** ₹1,23,456.00 */
export const inr = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : INR.format(Number(v)));

/** 1 → "1", 1.5 → "1.5", null → "—" */
export const days = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : String(Number(Number(v).toFixed(2)));

/** "2026-10-09T09:31:00" → "09:31" */
export const hhmm = (iso: string | null | undefined) => {
  if (!iso) return '';
  const m = /T?(\d{2}):(\d{2})/.exec(iso.includes('T') ? iso.split('T')[1] : iso);
  return m ? `${m[1]}:${m[2]}` : iso;
};

export const minutesLabel = (m: number | null | undefined) =>
  m === null || m === undefined ? '' : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;

export interface StatusStyle {
  label: string;
  short: string;
  bg: string;
  fg: string;
}

export const DAY_STATUS: Record<DayStatus, StatusStyle> = {
  PRESENT: { label: 'Present', short: 'P', bg: '#DCFCE7', fg: '#166534' },
  HALF_DAY: { label: 'Half day', short: 'H', bg: '#FEF3C7', fg: '#92400E' },
  ABSENT: { label: 'Absent', short: 'A', bg: '#FEE2E2', fg: '#991B1B' },
  PAID_LEAVE: { label: 'Paid leave', short: 'PL', bg: '#DBEAFE', fg: '#1E40AF' },
  UNPAID_LEAVE: { label: 'Unpaid leave', short: 'UL', bg: '#EDE9FE', fg: '#5B21B6' },
  HALF_LEAVE: { label: 'Half-day leave', short: '½L', bg: '#E0F2FE', fg: '#075985' },
  WEEKLY_OFF: { label: 'Weekly off', short: 'WO', bg: '#F3F4F6', fg: '#4B5563' },
  HOLIDAY: { label: 'Holiday', short: 'HO', bg: '#CCFBF1', fg: '#115E59' },
  NOT_EMPLOYED: { label: 'Not employed', short: '—', bg: '#FFFFFF', fg: '#9CA3AF' },
  UPCOMING: { label: 'Upcoming', short: '', bg: '#FAFAFA', fg: '#9CA3AF' },
  IN_PROGRESS: { label: 'In office', short: 'P…', bg: '#ECFDF5', fg: '#047857' },
};

export const statusStyle = (s: string | null | undefined): StatusStyle =>
  (s && DAY_STATUS[s as DayStatus]) || { label: s || '—', short: '—', bg: '#FFFFFF', fg: '#6B7280' };

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const METHOD_LABELS: Record<ComponentMethod, string> = {
  PCT_OF_GROSS: '% of monthly gross',
  PCT_OF_COMPONENT: '% of another component',
  BALANCE: 'Balance of gross',
  FIXED: 'Fixed amount',
  PCT_OF_EARNINGS: '% of earnings (if gross ≤ threshold)',
  PT_SLAB: 'Professional tax slab',
  EMPLOYEE_TDS: "Employee's monthly TDS",
  ADVANCE: 'Advance salary recovery',
};

export const KIND_LABELS: Record<ComponentKind, string> = {
  EARNING: 'Earning',
  DEDUCTION: 'Deduction',
  EMPLOYER: 'Employer contribution',
};

/** Today as yyyy-MM-dd in the browser's own time zone (toISOString would give UTC's date). */
export const localDate = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
